/**
 * Import Wizard State Machine Types
 *
 * This file contains all TypeScript types, enums, and helper functions
 * for the XState-based wizard state machine.
 */

import { CSVRow, ImportData, InternalColumns } from '../importer-types'

/**
 * Machine ID constant - used for absolute state references
 */
export const MACHINE_ID = 'importWizard'

/**
 * Wizard steps - the main navigation states (user-facing)
 */
export enum WizardStep {
  STEP1_CONFIRM = 'step1',
  STEP2_REVIEW = 'step2',
  STEP3_DECIDE = 'step3',
  STEP4_IMPORT = 'step4',
}

/**
 * Internal machine states - non-step states for async operations
 */
export enum InternalState {
  INITIALIZING = 'initializing',
  WAITING_FOR_PROCESS = 'waitingForProcess',
  REFRESHING = 'refreshing',
  ROUTING = 'routing',
  PROCESSING = 'processing',
  SAVING = 'saving',
  ERROR = 'error',
}

/**
 * Nested states within step states
 */
export enum NestedState {
  // Step 1 nested states
  CONFIRM = 'confirm',
  DESELECT_MODAL = 'deselectModal',

  // Step 3 nested states
  DECIDE = 'decide',
  CONFIRM_MODAL = 'confirmModal',

  // Step 4 nested states (deal validation)
  IMPORT = 'import',
  VALIDATING_DEALS = 'validatingDeals',
  DEAL_PRODUCT_MODAL = 'dealProductModal',
  DEAL_VALIDATION_MODAL = 'dealValidationModal',
  SAVING_VALIDATION_FIXES = 'savingValidationFixes',
}

/**
 * All event types the machine can receive
 */
export enum EventType {
  // Navigation
  GO_NEXT = 'GO_NEXT',
  GO_BACK = 'GO_BACK',

  // Step 1 modal
  OPEN_DESELECT_MODAL = 'OPEN_DESELECT_MODAL',
  CONFIRM_DESELECT = 'CONFIRM_DESELECT',
  CANCEL_DESELECT = 'CANCEL_DESELECT',

  // Step 3 modal
  OPEN_CONFIRM_MODAL = 'OPEN_CONFIRM_MODAL',
  CANCEL_CONFIRM = 'CANCEL_CONFIRM',

  // Step 4 deal validation
  VALIDATE_DEALS = 'VALIDATE_DEALS',
  CANCEL_DEAL_VALIDATION = 'CANCEL_DEAL_VALIDATION',
  DEAL_PRODUCT_SELECTED = 'DEAL_PRODUCT_SELECTED',
  DEALS_VALIDATION_FIXED = 'DEALS_VALIDATION_FIXED',

  // Processing
  START_PROCESS = 'START_PROCESS',
  SAVE_AND_RELOAD = 'SAVE_AND_RELOAD',

  // Polling events (from pollForIdle actor)
  POLL_COMPLETE = 'POLL_COMPLETE',
  POLL_ERROR = 'POLL_ERROR',

  // Error recovery
  RETRY = 'RETRY',
}

/**
 * Backend process types - maps to ProcessRunningChoices on backend
 */
export enum ProcessType {
  SETTING_COMPANIES = 'setting_companies',
  SETTING_TAGS = 'setting_tags',
  SETTING_DEALS = 'setting_deals',
  SETTING_CONTACTS = 'setting_contacts',
  SETTING_COMPANY_NOTES = 'setting_company_notes',
  MERGING = 'merging',
  CREATING_PREVIEW = 'creating_preview',
}

/**
 * Machine input - provided when creating the machine
 */
export interface WizardMachineInput {
  importNumber: number
}

/**
 * Context stored in the state machine
 */
export interface WizardContext {
  /** Import number from URL */
  importNumber: number

  /** Import name (filename) */
  importName: string

  /** Import data from API */
  importData: ImportData | null

  /** Skip matching mode */
  skipMatching: boolean

  /** Steps that are currently available for navigation */
  availableSteps: WizardStep[]

  /** Computed: Companies that are pre-matched (READY=true) */
  companiesPrematched: CSVRow[]

  /** Computed: Companies pending review (READY=false) */
  companiesPendingReview: CSVRow[]

  /** Computed: Companies with no match */
  companiesLeftUnmatched: CSVRow[]

  /** Computed: All unique companies */
  allCompanies: CSVRow[]

  /** Increments on each data refresh - used to reset local UI state */
  dataVersion: number

  /** Current backend process running (if any) */
  currentProcess: ProcessType | null

  /** Data to save before starting a process (set by START_PROCESS event) */
  processData: ImportData | null

  /** Error message (if any) */
  error: string | null

  /** Step to return to after saving (used by saving state) */
  returnToStep: WizardStep | null

  /** Current validation error data (when deal validation fails) */
  dealValidationData:
    | {
        type: 'mapping'
        missing: string[]
        found: string[]
        title: string
        itemName: string
        columnName: 'deal_account_owner' | 'deal_sales_stage'
      }
    | {
        type: 'product_required'
        title: string
        message: string
      }
    | null

  /** Flag to track if deal_product was added by applyDealProduct (for proper revert) */
  dealProductAddedByModal: boolean
}

/**
 * All events the machine can receive
 */
export type WizardEvent =
  // Navigation
  | { type: EventType.GO_NEXT }
  | { type: EventType.GO_BACK }

  // Step 1 modal
  | { type: EventType.OPEN_DESELECT_MODAL }
  | { type: EventType.CONFIRM_DESELECT }
  | { type: EventType.CANCEL_DESELECT }

  // Step 3 modal
  | { type: EventType.OPEN_CONFIRM_MODAL }
  | { type: EventType.CANCEL_CONFIRM }

  // Processing (user-initiated, with optional return step for explicit navigation)
  // data: optional ImportData to save before starting the process
  | {
      type: EventType.START_PROCESS
      processType: ProcessType
      originStep?: WizardStep
      data?: ImportData
    }

  // Save and reload (preserves current step)
  | {
      type: EventType.SAVE_AND_RELOAD
      data: ImportData
      skipMatching: boolean
      originStep: WizardStep
    }

  // Polling events (from pollForIdle actor)
  | { type: EventType.POLL_COMPLETE }
  | { type: EventType.POLL_ERROR; error: string }

  // Error recovery
  | { type: EventType.RETRY }

  // Step 4 deal validation
  | { type: EventType.VALIDATE_DEALS }
  | { type: EventType.CANCEL_DEAL_VALIDATION }
  | { type: EventType.DEAL_PRODUCT_SELECTED; productName: string }
  | {
      type: EventType.DEALS_VALIDATION_FIXED
      changes: Array<{ original: string; mapped?: string }>
      columnName: 'deal_account_owner' | 'deal_sales_stage'
    }

/**
 * Maps backend processRunning string to ProcessType enum
 */
export function mapProcessRunning(
  processRunning: string | null | undefined
): ProcessType | null {
  switch (processRunning) {
    case 'setting_companies':
      return ProcessType.SETTING_COMPANIES
    case 'setting_tags':
      return ProcessType.SETTING_TAGS
    case 'setting_deals':
      return ProcessType.SETTING_DEALS
    case 'setting_contacts':
      return ProcessType.SETTING_CONTACTS
    case 'setting_company_notes':
      return ProcessType.SETTING_COMPANY_NOTES
    case 'merging':
      return ProcessType.MERGING
    case 'creating_preview':
      return ProcessType.CREATING_PREVIEW
    default:
      return null
  }
}

/**
 * Get human-readable message for a process type
 */
export function getProcessMessage(process: ProcessType | null): string {
  switch (process) {
    case ProcessType.SETTING_COMPANIES:
      return 'Setting up companies...'
    case ProcessType.SETTING_TAGS:
      return 'Importing tags...'
    case ProcessType.SETTING_DEALS:
      return 'Importing deals...'
    case ProcessType.SETTING_CONTACTS:
      return 'Importing contacts...'
    case ProcessType.SETTING_COMPANY_NOTES:
      return 'Importing company notes...'
    case ProcessType.MERGING:
      return 'Merging companies...'
    case ProcessType.CREATING_PREVIEW:
      return 'Creating preview...'
    default:
      return 'Processing...'
  }
}

/**
 * Compute company categorization from import data
 */
export function computeCompanyData(importData: ImportData | null): {
  companiesPrematched: CSVRow[]
  companiesPendingReview: CSVRow[]
  companiesLeftUnmatched: CSVRow[]
  allCompanies: CSVRow[]
  areAllRowsSet: boolean
} {
  if (!importData) {
    return {
      companiesPrematched: [],
      companiesPendingReview: [],
      companiesLeftUnmatched: [],
      allCompanies: [],
      areAllRowsSet: false,
    }
  }

  const companyGroups = new Map<string, CSVRow[]>()
  const allCompanies: CSVRow[] = []
  const companiesPrematched: CSVRow[] = []
  const companiesPendingReview: CSVRow[] = []
  const companiesLeftUnmatched: CSVRow[] = []

  let readyRowCount = 0
  let setRowCount = 0

  for (const row of importData.rows) {
    const hash = row[InternalColumns.COMPANY_HASH]
    const isReady = row[InternalColumns.READY]
    const hasMatch =
      row[InternalColumns.MATCHED_CHAIN] || row[InternalColumns.MATCHED_DOOR]
    const isSet =
      row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
      row[InternalColumns.IS_CHAIN_MERGED]

    if (isReady) readyRowCount++
    if (isSet) setRowCount++

    // First time seeing this company?
    if (!companyGroups.has(hash)) {
      companyGroups.set(hash, [])
      allCompanies.push(row)

      // Categorize company
      if (isReady) {
        companiesPrematched.push(row)
      } else {
        companiesPendingReview.push(row)
        if (!hasMatch) {
          companiesLeftUnmatched.push(row)
        }
      }
    }

    companyGroups.get(hash)!.push(row)
  }

  const areAllRowsSet = setRowCount === readyRowCount && setRowCount > 0

  return {
    companiesPrematched,
    companiesPendingReview,
    companiesLeftUnmatched,
    allCompanies,
    areAllRowsSet,
  }
}

/**
 * Compute available steps based on company data
 */
export function computeAvailableSteps(
  companyData: ReturnType<typeof computeCompanyData>,
  skipMatching: boolean
): WizardStep[] {
  // If all rows are already set, only step 4 is available
  if (companyData.areAllRowsSet) {
    return [WizardStep.STEP4_IMPORT]
  }

  const steps: WizardStep[] = []

  // Step 1: Only if there are pre-matched companies to confirm
  if (companyData.companiesPrematched.length > 0 && !skipMatching) {
    steps.push(WizardStep.STEP1_CONFIRM)
  }

  // Step 2: Only if there are pending companies to review
  if (companyData.companiesPendingReview.length > 0 && !skipMatching) {
    steps.push(WizardStep.STEP2_REVIEW)
  }

  // Step 3 & 4: Always available
  steps.push(WizardStep.STEP3_DECIDE)
  steps.push(WizardStep.STEP4_IMPORT)

  return steps
}
